import React, { Component, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import ellipsis from "../../../assets/Loading/Ellipsis.svg";
import { FormattedMessage } from "react-intl";
import styles from "../../../styles/browseStory.js";
import WizardToggleButton from "../Wizards/WizardToggleButton";
import bookshareLogo from '../../../assets/ScreenShots/bookshare-Logo.png';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Typography,
  Toolbar,
  Tooltip,
  InputAdornment,
  TextField,
  withStyles,
  Tab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Button,
  Box,
  AppBar,
  MenuItem,
} from "@material-ui/core";
import {
  CheckCircle,
  Close,
  CloudDownload,
  CodeSharp,
  Search,
} from "@material-ui/icons";
import {
  addStoryToStudent,
  clearBookShareLogin,
  downloadBook,
  fetchUserSkillDatabanks,
  getAllBooks,
  getBooksForStudent,
  getDatabank,
  getStoryList,
  searchForBooks,
  getBookShareIdentity,
  reinitializeBookShareIdentity,
  searchForBooksStart,
} from "../../../redux/actions/index.js";
import { stableSort, getSorting } from "../../../helpers";
import Slider from "../../../Social/NetflixSlider";
import BookShareTable from "../Shared/BookShareTable";
import DisplayStoryList from "./DisplayStoryList";
import { RESET_IMPORT_MODAL, SEARCH_FOR_BOOKS_FAIL, REINITIALIZE_IMPORT_MODAL } from "../../../redux/constants";
import BookShareCredentialsLogin from "./BookShareCredentialsLogin";
import LoadingModal from "./LoadingModal";

const rows = [
  // Bookshare table headers
  { id: 'titleImage', label: <FormattedMessage id="General.image" />},
  { id: "title", label: <FormattedMessage id="General.title" /> },
  { id: "author", label: <FormattedMessage id="General.author" /> },
  { id: "synopsis", label: <FormattedMessage id="General.synopsis" /> },
  { id: "isbn", label: <FormattedMessage id="General.isbn" /> },
];

const dBRows = [
  // Searchbar table headers; for our processed OED Catalog
  { id: 'titleImageSrc', label: <FormattedMessage id="General.image" />, searchable: true },
  { id: "name", label: <FormattedMessage id="General.title" />, searchable: true },
  { id: "author", label: <FormattedMessage id="General.author" />, searchable: true },
  { id: "isbn", label: <FormattedMessage id="General.isbn" />, searchable: true },
  { id: "source", label: <FormattedMessage id="General.source" />, searchable: true },
];

const mapState = ({
  platform: { books, bookShareList, storyList, databank, skillDatabanks, student },
  shared: { user, bookShareIdentity },
}) => {
  return {
    books,
    bookShareList,
    storyList,
    databank,
    skillDatabanks,
    student,
    user,
    bookShareIdentity,
  };
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  })
  return ref.current;
}

const BrowseBook = (props) => {
  // Helper function to determine default level based on student's buddybooks level
  const getDefaultLevel = (student) => {
    if (!student) {
      return 'All Levels';
    }
    
    // Check for both possible key names (buddybooksLevel or buddyBooksLevel)
    const buddyBooksLevel = student.buddybooksLevel || student.buddyBooksLevel;
    
    // If no key exists or no value, default to All Levels
    if (buddyBooksLevel === null || buddyBooksLevel === undefined) {
      return 'All Levels';
    }
    
    // Convert to number and validate
    const levelNumber = Number(buddyBooksLevel);
    if (isNaN(levelNumber) || levelNumber < 1) {
      return 'All Levels';
    }
    
    // Check if the level exists in our dropdown options
    // This makes it future-proof for levels beyond 6
    const targetLevel = `Level ${levelNumber}`;
    const availableLevels = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5', 'Level 6'];
    
    if (availableLevels.includes(targetLevel)) {
      return targetLevel;
    }
    
    // If level doesn't exist in our current options, default to All Levels
    return 'All Levels';
  };

  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [browseTabClicked, setBrowseTabClicked] = useState(true);
  const [recomendTabClicked, setRecomendTabClicked] = useState(true);
  const [lexileTabClicked, setLexileTabClicked] = useState(false);
  const [readingTabClicked, setReadingTabClicked] = useState(false);
  const [searchTabClicked, setSearchTabClicked] = useState(false);
  const [searchFromBookShare, setSearchFromBookShare] = useState(false);
  const [searchField, setSearchField] = useState("title");
  const [searchCategory, setSearchCategory] = useState("name");
  const [imported, setImported] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [noResultFound, setNoResultFound] = useState(false);
  const [filteredDB, setFilteredDB] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [loginRequired, setLoginRequired] = useState(false);
  const [showBookshareAccess, setShowBookshareAccess] = useState(false);
  const [includeTitleImage, setIncludeTitleImage] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(() => getDefaultLevel(props.student));

  const {downloadBook, classes, bookShareList, storyList, user, databank, student, isOpenFromBooksPage, setIsOpenFromBooksPage } = props


  const prevProps = usePrevious(props);

  const dispatch = useDispatch();

  // Callback function to refresh books data after import
  const refreshBooksData = useCallback(() => {
    if (props.id) {
      dispatch(getBooksForStudent(props.id));
    }
  }, [dispatch, props.id]);

  const browseStoryList = () => {
    setBrowseTabClicked(true);
    setRecomendTabClicked(true);
    setLexileTabClicked(false);
    setReadingTabClicked(false);
    setSearchTabClicked(false);
    setSearchFromBookShare(false);
    setSearchValue("");

    dispatch(getStoryList(props.id));
  };

  const browseLexileStoryList = () => {
    setBrowseTabClicked(true);
    setRecomendTabClicked(false);
    setLexileTabClicked(true);
    setReadingTabClicked(false);
    setSearchValue("");
    setIncludeTitleImage(false);
    dispatch({ type: RESET_IMPORT_MODAL });
  };

  const browseBooksByLevel = () => {
    setBrowseTabClicked(true);
    setRecomendTabClicked(false);
    setLexileTabClicked(true);
    setReadingTabClicked(false);
    setSearchValue("");
    setIncludeTitleImage(false);
    dispatch({ type: RESET_IMPORT_MODAL });
    dispatch(getStoryList(props.id));
  };

  const browseStudentStoryList = () => {
    setSearchTabClicked(false);
    setRecomendTabClicked(false);
    setLexileTabClicked(false);
    setReadingTabClicked(true);
    setSearchValue("");
  };

  const searchEnable = () => {
    setSearchTabClicked(true);
    setBrowseTabClicked(false);
  }

  const handleSearch = useCallback((...args) => {
    if (searchFromBookShare) {
      if (user.hasBookShareCredentials) {
        dispatch(searchForBooksStart());
        dispatch(searchForBooks(searchValue, searchField));
        setNoResultFound(false);
        setRecomendTabClicked(false);
        setLexileTabClicked(false);
        setReadingTabClicked(false);
        setIncludeTitleImage(true);
      } else {
        setLoginRequired(true);
        setNoResultFound(true);
      }
    } else if (args && args[0] === "first") {
      setNoResultFound(false);
      setFilteredDB(databank);
      setRecomendTabClicked(false);
      setReadingTabClicked(false);
    } else {
      let filteredDB =
        searchValue &&
        props.databank.filter((item) => {
          const searchableKeys = [{ id: "name" }, { id: "author" }];
          return searchableKeys.some((key) => {
            if (item[key.id]) {
              return item[key.id]
                .toLowerCase()
                .includes(searchValue.toLowerCase());
            } else {
              return false;
            }
          });
        });
      let isFiltered = filteredDB && filteredDB.length > 0;
      setNoResultFound(!isFiltered);
      setFilteredDB(isFiltered ? filteredDB : []);
      setRecomendTabClicked(false);
      setLexileTabClicked(true);
      setReadingTabClicked(false);
      setIncludeTitleImage(false);
    }
  }, [searchFromBookShare, user, databank, searchValue, searchField]);

  // const clickSearchTab = useCallback((storyList) => {
    //no longer using Search tab to show full catalog
  //   setSearchTabClicked(true);
  //   setBrowseTabClicked(false);
  //   setRecomendTabClicked(false);
  //   setLexileTabClicked(false);
  //   setReadingTabClicked(false);
  //   if (searchValue) {
  //     handleSearch("", searchValue, storyList);
  //   } else {
  //     handleSearch("first", searchValue, storyList);
  //   }
  // }, [searchValue, handleSearch]);

  const closeWizard = () => {
    dispatch({ type: SEARCH_FOR_BOOKS_FAIL });
    dispatch({ type: REINITIALIZE_IMPORT_MODAL });
    resetAll()
    setIsOpenFromBooksPage(false)
    setOpen(false);
  };
  const resetAll = () => {
    setSelected([]);
    setOpen(false);
    setSearchValue("");
    setBrowseTabClicked(true);
    setRecomendTabClicked(true);
    setLexileTabClicked(false);
    setReadingTabClicked(false);
    setSearchTabClicked(false);
    setSearchFromBookShare(false);
    setSearchField("title");
    setSearchCategory("name");
    setImported([]);
    setOpenModal(false);
    setModalType("");
    setNoResultFound(false);
    setFilteredDB(null);
    setFilteredData([]);
    setLoginRequired(false);
    setShowBookshareAccess(false);
    setIncludeTitleImage(false);
    setSelectedLevel(getDefaultLevel(props.student));
  };

  const changeSearchData = (toggle) => {
    setSearchFromBookShare(toggle);
    setTimeout(() => {
      if (user && !user.hasBookShareCredentials) {
        setLoginRequired(true);
      } else if (toggle && searchValue) {
        dispatch(searchForBooksStart());
        dispatch(searchForBooks(searchValue, searchField));
        setNoResultFound(false);
        setRecomendTabClicked(false);
        setLexileTabClicked(false);
        setReadingTabClicked(false);
        setIncludeTitleImage(true);
      }
    }, 200);
  };
  const closeLoginModal = () => {
    setLoginRequired(false);
  };
  const handleOpenModal = () => {
    setOpenModal(true);
    setModalType("Downloading");
  };

  const isLoading = useMemo(() => {
    let _data = props.bookShareList;
    if (_data && _data.loading) return true;
    return false
  }, [bookShareList])

  const data = useMemo(() => {
    let _data = searchFromBookShare ? props.bookShareList : filteredDB;
    let objData = [];

    if (_data && !_data.loading) {
      // Ensure _data is an array before filtering
      _data = Array.isArray(_data) ? _data : [];
      objData = _data.filter((_d) => _d.source === "ObjectiveEd");

      if (props.user && props.user.type === "Individual" && !props.user.hasBookShareCredentials) {
        return objData;
      }
      return _data;
    }
    return objData;
  }, [searchFromBookShare, bookShareList, filteredDB]);

  useEffect(() => {
    dispatch(getAllBooks(props.id));
    dispatch(getStoryList(props.id));
    dispatch(fetchUserSkillDatabanks(false));
    dispatch(getDatabank(props.id));
    dispatch(getBookShareIdentity());

    // Cleanup function to reset bookshare identity when component unmounts
    return () => {
       dispatch(reinitializeBookShareIdentity());
       resetAll();
       dispatch({ type: SEARCH_FOR_BOOKS_FAIL });
       dispatch({ type: REINITIALIZE_IMPORT_MODAL });
    };

  }, [])

  useEffect(() => {
    // This is to prevent the page from scrolling when the dialog is open
    const htmlElement = document.documentElement; // Gets the HTML element
    
    if (open) {
      htmlElement.classList.add('dialog-open');
    } else {
      htmlElement.classList.remove('dialog-open');
    }
    
    return () => {
      htmlElement.classList.remove('dialog-open');
    };
  }, [open]);

  useEffect(() => {
    isOpenFromBooksPage ? setOpen(true) : setOpen(false)
  }, [isOpenFromBooksPage])

  useEffect(() => {
    if(! prevProps || (props.user !== prevProps.user)){
      if(searchValue !=="" && props.user.hasBookShareCredentials){
          dispatch(searchForBooksStart())
          dispatch(searchForBooks(searchValue, searchField));
      }
    }
  }, [props, searchValue, searchField])

  useEffect(() => {
    if (downloadBook) {
      const {isBookDownloaded } = downloadBook;
      if (!isBookDownloaded) {
        setOpenModal(true);
        setModalType('DownloadFail')
      }
    }
  }, [downloadBook])

  useEffect(() => {
    if (!bookShareList || !bookShareList.length) {
      setNoResultFound(true)
    }
  }, [bookShareList])

  // Update selectedLevel when student data changes
  useEffect(() => {
    console.log('BrowseBook: Student data changed:', student);
    if (student) {
      const buddyBooksLevel = student.buddybooksLevel || student.buddyBooksLevel;
      const defaultLevel = getDefaultLevel(student);
      console.log('BrowseBook: Setting default level to:', defaultLevel, 'based on buddyBooksLevel:', buddyBooksLevel);
      setSelectedLevel(defaultLevel);
    }
  }, [student]);

  const logOutBookshare = () => {
    dispatch(clearBookShareLogin());
    setTimeout(() => {
      dispatch(reinitializeBookShareIdentity());
    }, 1000); 
    closeWizard();
  };

  return (
    <>
      <Dialog
        open={open}
        classes={{ paper: classes.browseDialog }}
        maxWidth="lg"
        fullWidth={true}
        onClose={closeWizard}
      >
        <Paper style={{ width: "100%", "overflowY": "hidden" }}>
          <AppBar position="sticky">
            <Toolbar 
            classes={{ root: classes.toolbar }}
            style={{
              gridTemplateColumns: searchFromBookShare 
                ? 'auto 1fr 3fr auto' // When Bookshare is active: logo | browse | search | close
                : '1fr 3fr auto',     // Default layout: browse | search | close
              backgroundColor: searchFromBookShare ? '#ffa502' : undefined
            }}
            >
              {searchFromBookShare ? (
                  <Tooltip 
                    title="Books on this table can be requested from Bookshare"
                    placement="right"
                  >
                    <img 
                      src={bookshareLogo}
                      alt="Bookshare"
                      style={{ 
                        height: "30px", 
                        width: "auto",
                        margin: "0",
                        cursor: "pointer",
                        padding: "5px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "6px",
                        gridColumn: "1" // Explicitly place in first column
                      }} 
                    />
                  </Tooltip>
              ) : (
                <Tab
                  label="Browse"
                  className={
                    browseTabClicked ? classes.clicked : classes.unClicked
                  }
                  onClick={browseStoryList}
                  style={{ gridColumn: searchFromBookShare ? "2" : "1" }} // Adjust column based on layout
                />
              )}
              <div className={classes.actions} style={{ gridColumn: searchFromBookShare ? "3" : "2" }}>

                
                {/* <Tab
                  label="Search" //no longer using Search tab to show full catalog
                  className={
                    searchTabClicked ? classes.clicked : classes.unClicked
                  }
                  onClick={() => {
                    clickSearchTab(props.bookShareList);
                  }}
                /> */}
                <TextField
                  variant="outlined"
                  id="table-search"
                  aria-label="search input"
                  type="search"
                  placeholder={searchFromBookShare ? "Search Bookshare" : "Search Catalog"}
                  disabled={isLoading}
                  InputProps={{
                    inputProps: {
                      "aria-label": "search input",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            searchEnable()
                            handleSearch()
                          }}
                        />
                      </InputAdornment>
                    ),
                    className: classes.searchBar,
                  }}
                  onChange={(ev) => {
                    setSearchValue(ev.target.value);
                  }}
                  value={searchValue}
                  onKeyUp={(event) => {
                    if (event.key === "Enter") {
                      searchEnable()
                      handleSearch()
                    };
                  }}
                />
              </div>
              <IconButton 
                onClick={closeWizard} 
                className={classes.close}
                style={{ gridColumn: searchFromBookShare ? "4" : "3" }}
              >
                <Close />
              </IconButton>
            </Toolbar>
            {browseTabClicked && (
              <div
                className={classes.browseBtnWrapper}
                style={{ 
                  backgroundColor: "white", 
                  color: "grey",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 16px"
                }}
              >
                <Tab
                  label="Recommendations"
                  className={
                    recomendTabClicked
                      ? classes.innerTabClicked
                      : classes.innerTabUnclicked
                  }
                  onClick={browseStoryList}
                />
                
                {/* Student's Current Level Display */}
                {student && student.firstName && (student.buddybooksLevel || student.buddyBooksLevel) && (
                  <div style={{ 
                    color: "#666", 
                    fontSize: "16px", 
                    fontWeight: "normal",
                    fontFamily: "Arial, sans-serif",
                    textAlign: "center",
                    flex: "1"
                  }}>
                    {student.firstName}'s current BuddyBooks level: {student.buddybooksLevel || student.buddyBooksLevel}
                  </div>
                )}
                
                <Tab
                  label="Books by Level"
                  className={
                    lexileTabClicked
                      ? classes.innerTabClicked
                      : classes.innerTabUnclicked
                  }
                  onClick={browseBooksByLevel}
                />
              </div>
            )}
          </AppBar>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: searchTabClicked ? "74vh" : "65vh",
            }}
          >
            <DialogContent style={{ flex: "1", overflowY: "auto" }}>
              {/* just the search table, not really Bookshare, but uses the table component */}
              {searchTabClicked && (
                <BookShareTable
                  rows={searchFromBookShare ? rows : dBRows}
                  data={data}
                  imported={imported}
                  noResultFound={noResultFound}
                  changeSearchData={changeSearchData}
                  includeTitleImage={searchFromBookShare}
                  bookShareSearchToggle={searchFromBookShare}
                  lexileTabClicked={searchFromBookShare ? false : true}
                  storyId={props.id}
                  toggleSearch={searchFromBookShare}
                  searchFromBookShare={searchFromBookShare}
                  closeWizard={closeWizard}
                  hasBookShareCredentials={props.user.hasBookShareCredentials}
                  openModal={handleOpenModal}
                />
              )}
              {/* Level selector dropdown - only show when Books by Level tab is active */}
              {lexileTabClicked && !searchTabClicked && (
                <div style={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    select
                    label="Select Level"
                    value={selectedLevel}
                    onChange={e => setSelectedLevel(e.target.value)}
                    variant="outlined"
                    margin="dense"
                    style={{ minWidth: 200 }}
                  >
                    <MenuItem value="All Levels">All Levels</MenuItem>
                    <MenuItem value="Level 1">Level 1 (early elementary)</MenuItem>
                    <MenuItem value="Level 2">Level 2 (early elementary)</MenuItem>
                    <MenuItem value="Level 3">Level 3 (upper elementary)</MenuItem>
                    <MenuItem value="Level 4">Level 4 (upper elementary)</MenuItem>
                    <MenuItem value="Level 5">Level 5 (middle school)</MenuItem>
                    <MenuItem value="Level 6">Level 6 (middle school)</MenuItem>
                  </TextField>
                </div>
              )}
              {/* DisplayStoryList calls BookShareTable for either Grade Level table or Bookshare table */}
              {browseTabClicked && (() => {
                // First filter by level if a specific level is selected
                let filteredStoryList = lexileTabClicked && selectedLevel !== 'All Levels'
                  ? storyList.filter(list => list.type === selectedLevel)
                  : storyList;
                
                // Apply Bookshare filtering to both Recommendations and Books by Level tabs
                // Only show locked "Y" lists if user has Bookshare credentials
                filteredStoryList = filteredStoryList.filter(list => {
                  // If locked is "Y", only show if user has Bookshare credentials
                  if (list.locked === 'Y') {
                    return props.user && props.user.hasBookShareCredentials;
                  }
                  // If locked is "N" or null, always show
                  return true;
                });
                
                // Show no results message if filtering by level and no matches found
                if (lexileTabClicked && selectedLevel !== 'All Levels' && filteredStoryList.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <Typography variant="h6">
                        No books found for {selectedLevel}
                      </Typography>
                      <Typography variant="body2" style={{ marginTop: '8px' }}>
                        Try selecting a different level or "All Levels" to see all available books.
                      </Typography>
                    </div>
                  );
                }
                
                return (
                  <DisplayStoryList
                    id={props.id}
                    type={
                      recomendTabClicked
                        ? "Recomendations"
                        : lexileTabClicked
                        ? "LexileStory"
                        : "StudentStory"
                    }
                    storyList={filteredStoryList}
                    imported={imported}
                    storyId={props.id}
                    closeWizard={closeWizard}
                    refreshBooksData={refreshBooksData}
                  />
                );
              })()}
            </DialogContent>
            {props.user && (
              <DialogActions position="fixed" bottom="0">
                <div
                  className={classes.browseBtnWrapper}
                  style={{
                    align: "center",
                    backgroundColor: "white",
                    color: "grey",
                    width: "100%",
                  }}
                >
                  {!props.user.hasBookShareCredentials ? (
                    <Tab
                      label="Login to Bookshare"
                      className={classes.innerTabClicked}
                      onClick={() => setLoginRequired(true)}
                    />
                  ) : (
                    <React.Fragment>
                      <Tab
                        label="Logout from Bookshare"
                        className={classes.innerTabClicked}
                        onClick={logOutBookshare}
                      />
                      <div style={{ paddingTop: "15px", fontFamily: "Arial, sans-serif" }}>
                        Logged in As: {props.bookShareIdentity && typeof props.bookShareIdentity === 'string' ? props.bookShareIdentity : ''}
                      </div>
                    </React.Fragment>
                  )}
                </div>
              </DialogActions>
            )}
          </div>
        </Paper>
      </Dialog>
      {loginRequired && (
        <BookShareCredentialsLogin
          openLoading={loginRequired}
          exitFunc={() => {
            setLoginRequired(false);
          }}
        />
      )}
      {openModal && (
        <LoadingModal
          openLoading={openModal}
          type={modalType}
          exitFunc={() => {
            setOpenModal(false);
            setModalType("");
          }}
        />
      )}
      {showBookshareAccess && (
        <Dialog
          open={showBookshareAccess}
          onClose={() => {
            setShowBookshareAccess(false);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogContent style={{ overflowY: "auto" }}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={6}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src="https://www.bookshare.org/cms/sites/default/files/styles/panopoly_image_original/public/images/media/current_version_bookshare_logo_transparent_background_with_tagline_horizontal_color.png?itok=2lZD6Hcw"
                  alt="Image 1"
                  style={{ width: "66%", height: "auto" }}
                />
              </Grid>
              <Grid
                item
                xs={6}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src="https://s3.us-east-2.amazonaws.com/oe-ui/BB-logo-oed.png"
                  alt="Image 2"
                  style={{ width: "66%", height: "auto" }}
                />
              </Grid>
            </Grid>
            <div>
              Bookshare is an ebook library of 1 million+ books that are
              available to qualifying individuals. BuddyBooks partnership with
              Bookshare allows those with a Bookshare account to link their
              accounts and read books from Bookshare's catalogue within
              BuddyBooks. For more information on how to qualify for a Bookshare
              account, click here:{" "}
              <a
                target={"_blank"}
                href="https://www.objectiveed.com/bookshare-test"
              >
                Bookshare Qualification
              </a>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export class OldBrowseBook extends Component {

//     state = {
//         selected: {},
//         open: false,
//         searchValue: '',
//         filteredData: [],
//         browseTabClicked: true,
//         recomendTabClicked: true,
//         lexileTabClicked: false,
//         readingTabClicked: false,
//         searchTabClicked: false,
//         searchFromBookShare: false,
//         searchField: 'title',
//         searchCategory: 'name',
//         imported: [],
//         openModal: false,
//         modalType: '',
//         noResultFound: false,
//         filteredDB: null,
//         loginRequired: false
//     };

//     componentDidMount() {
//         this.props.dispatch(getAllBooks(this.props.id));
//         this.props.dispatch(getStoryList(this.props.id));
//         this.props.dispatch(fetchUserSkillDatabanks(false));
//         this.props.dispatch(getDatabank(this.props.id));
//         this.props.dispatch(getBookShareIdentity());
//     };

//     componentDidUpdate(prevProps){
//         if(this.props.user !== prevProps.user){
//             if(this.state.searchValue !=="" && this.props.user.hasBookShareCredentials){
//                 this.props.dispatch(searchForBooks(this.state.searchValue, this.state.searchField));
//             }
//         }
//     }

//     componentWillReceiveProps(nextProps, nextContext) {
//         if (nextProps.storyList !== this.props.storyList && nextProps.storyList) {
//             console.log('storyList browse', nextProps.storyList);
//         }

//         if (nextProps.downloadBook !== this.props.downloadBook && nextProps.downloadBook.isBookDownloaded) {
//             const { isBookDownloaded } = nextProps.downloadBook;
//             if (!isBookDownloaded)
//                 this.setState({ openModal: true, modalType: 'DownloadFail' });
//         }

//         if (nextProps.bookShareList !== this.props.bookShareList && !nextProps.bookShareList.length) {
//             this.setState({ noResultFound: true });
//         }
//         if (this.props.user && nextProps.user && nextProps.user != this.props.user && nextProps.user.hasBookShareCredentials) {
//             if (this.state.searchFromBookShare && this.state.searchValue) {
//                 this.handleSearch();
//             }
//         }
//     }

//     loginBookshare = () => {
//         this.setState({
//             loginRequired: true
//         });
//     }

//     logOutBookshare = () => {
//         this.props.dispatch(clearBookShareLogin());
//         this.props.dispatch(getBookShareIdentity());
//     }

//     openBookshareAccess = () => {
//         this.setState({
//             showBookshareAccess: true
//         })
//     }

//     closeBookshareAccess = () => {
//         this.setState({
//             showBookshareAccess: false,
//         })
//     }

//     browseStoryList = () => {
//         this.setState({
//             browseTabClicked: true,
//             recomendTabClicked: true,
//             lexileTabClicked: false,
//             readingTabClicked: false,
//             searchTabClicked: false,
//             searchFromBookShare: false,
//             searchValue: ''
//         })
//         this.props.dispatch(getStoryList(this.props.id));
//     };

//     browseLexileStoryList = () => {
//         this.setState({
//             searchTabClicked: false,
//             recomendTabClicked: false,
//             lexileTabClicked: true,
//             readingTabClicked: false,
//             searchValue: '',
//             includeTitleImage: false
//         });
//         this.props.dispatch({ type: RESET_IMPORT_MODAL })
//     };

//     browseStudentStoryList = () => {
//         this.setState({
//             searchTabClicked: false,
//             recomendTabClicked: false,
//             lexileTabClicked: false,
//             readingTabClicked: true,
//             searchValue: ''
//         })
//     };

//     clickSearchTab = (text, storyList) => {
//         this.setState({
//             searchTabClicked: true,
//             browseTabClicked: false,
//             recomendTabClicked: false,
//             lexileTabClicked: false,
//             readingTabClicked: false,
//         });
//         this.handleSearch("first",text, storyList);
//     };

//     // importSkill = (book) => {
//     //     console.log('download from import skill');
//     //     if(this.state.searchFromBookShare) {
//     //         this.props.dispatch(downloadBook(book.id, this.props.id));
//     //     } else {
//     //         this.props.dispatch(addStoryToStudent(this.props.id, book.id));
//     //     }
//     //     this.setState(state => ({ imported: [...state.imported, book.id], openModal: true, modalType: 'Downloading' }))
//     // };

//     handleSearch = (...args) => {
//         if (this.state.searchFromBookShare ) {
//             if(this.props.user.hasBookShareCredentials){
//                 this.props.dispatch(searchForBooks(this.state.searchValue, this.state.searchField));
//                 this.setState({
//                     noResultFound: false,
//                     searchTabClicked: true,
//                     browseTabClicked: false,
//                     recomendTabClicked: false,
//                     lexileTabClicked: false,
//                     readingTabClicked: false,
//                     includeTitleImage: true,
//                 });}
//                 else{
//                     this.setState({
//                         loginRequired:true,
//                         noResultFound: true,})
//                 }
//         }
//         else if(args && args[0] == "first"){
//             this.setState({
//                 noResultFound:  false,
//                 filteredDB: this.props.databank,
//                 searchTabClicked: true,
//                 browseTabClicked: false,
//                 recomendTabClicked: false,
//                 readingTabClicked: false,
//             });
//         }
//         else {
//             let filteredDB = this.state.searchValue && this.props.databank.filter(item => {
//                 const searchableKeys = [{ id: 'name' }, { id: 'author' }]
//             return searchableKeys.some(key => {
//                 if (item[key.id]) {
//                     return item[key.id].toLowerCase().includes(this.state.searchValue.toLowerCase())
//                 }
//                 else {
//                     return false
//                 }
//             })
//             });
//             let isFiltered = filteredDB && filteredDB.length > 0;
//             this.setState({
//                 noResultFound: isFiltered ? false : true,
//                 filteredDB: isFiltered ? filteredDB : [],
//                 searchTabClicked: true,
//                 browseTabClicked: false,
//                 recomendTabClicked: false,
//                 lexileTabClicked: true,
//                 readingTabClicked: false,
//                 includeTitleImage: false
//             });
//         }
//     };

//     openWizard = () => {
//         this.setState({ open: true })
//     };

//     closeWizard = () => {
//         this.setState({
//             selected: {},
//             open: false,
//             searchValue: '',
//             filteredData: [],
//             browseTabClicked: true,
//             recomendTabClicked: true,
//             lexileTabClicked: false,
//             readingTabClicked: false,
//             searchTabClicked: false,
//             searchFromBookShare: false,
//             imported: [],
//             openModal: false,
//             modalType: '',
//             noResultFound: false,
//             filteredDB: null
//         });
//     };

//     changeSearchData = (toggle) => {
//         this.setState({ searchFromBookShare: toggle });
//         setTimeout(() => {
//             if (this.props.user && !this.props.user.hasBookShareCredentials) {
//                 this.setState({ loginRequired: true });
//             }
//             else if (this.state.searchFromBookShare && this.state.searchValue) {
//                 console.log("Book toggle api call", this.state);
//                 this.handleSearch();
//             }
//         }, 200)
//     }
//     closeLoginModal = () => {
//         this.setState({ loginRequired: false });
//     }
//     openModal=()=>{
//         this.setState(state => ({ openModal: true, modalType: 'Downloading' }))
//     }

//     render() {
//         const { classes, storyList, bookShareList, databank, user } = this.props;
//         console.log('storyList', storyList);
//         const { open, filteredDB, loginRequired, showBookshareAccess } = this.state;
//         let data = this.state.searchFromBookShare ? bookShareList : filteredDB;
//         const objData = data && data.filter(_data => _data.source == 'ObjectiveEd');
//         if (user && user.type == 'Individual' && !user.hasBookShareCredentials) {
//             data = objData;
//         }
//         return (
//             <>
//                 <Dialog open={open} classes={{ paper: classes.browseDialog }} maxWidth='lg' fullWidth={true} onClose={this.closeWizard}>
//                     <Paper style={{ "width": "100%", "overflow-y":"hidden"}}>
//                         <AppBar position='sticky'>
//                             <Toolbar classes={{ root: classes.toolbar }}>
//                                 <Tab
//                                     label="Browse"
//                                     className={this.state.browseTabClicked ? classes.clicked : classes.unClicked}
//                                     onClick={this.browseStoryList}
//                                 />
//                                 <div className={classes.actions}>
//                                     <Tab
//                                         label="Search"
//                                         className={this.state.searchTabClicked ? classes.clicked : classes.unClicked}
//                                         onClick={() => { this.clickSearchTab(this.state.searchValue, bookShareList) }}
//                                     />
//                                     <TextField
//                                         variant='outlined'
//                                         id='table-search'
//                                         aria-label='search input'
//                                         type='search'
//                                         placeholder='Search our Books!'
//                                         InputProps={{
//                                             inputProps: {
//                                                 'aria-label': 'search input'
//                                             },
//                                             endAdornment: (
//                                                 <InputAdornment position='end'>
//                                                     <Search style={{ cursor: 'pointer' }} onClick={this.handleSearch} />
//                                                 </InputAdornment>
//                                             ),
//                                             className: classes.searchBar,
//                                         }}
//                                         onChange={(ev) => {
//                                             this.setState({ searchValue: ev.target.value });
//                                         }}
//                                         value={this.state.searchValue}

//                                         onKeyUp={(event) => {
//                                             if (event.key == 'Enter')
//                                                 this.handleSearch();
//                                         }}
//                                     />
//                                 </div>
//                                 <IconButton onClick={this.closeWizard} className={classes.close}>
//                                     <Close />
//                                 </IconButton>
//                             </Toolbar>
//                             {this.state.browseTabClicked &&
//                                 <div className={classes.browseBtnWrapper} style={{ backgroundColor: "white", color: "grey" }}>
//                                     <Tab
//                                         label="Recommendations"
//                                         className={this.state.recomendTabClicked ? classes.innerTabClicked : classes.innerTabUnclicked}
//                                         onClick={this.browseStoryList}
//                                     />
//                                     <Tab
//                                         label="Books by Grade Level"
//                                         className={this.state.lexileTabClicked ? classes.innerTabClicked : classes.innerTabUnclicked}
//                                         onClick={this.browseLexileStoryList}
//                                     />
//                                     <Tab
//                                         label="Your Reading Lists"
//                                         className={this.state.readingTabClicked ? classes.innerTabClicked : classes.innerTabUnclicked}
//                                         onClick={this.browseStudentStoryList}
//                                     />
//                                 </div>
//                             }
//                         </AppBar>
//                         <div style={{ display: 'flex', flexDirection: 'column', height: this.state.searchTabClicked ? '74vh' : '65vh' }}>
//                         <DialogContent style={{ flex: '1', overflowY: 'auto' }}>

//                         {this.state.searchTabClicked &&
//                             <BookShareTable
//                                 rows={this.state.searchFromBookShare ? rows : dBRows}
//                                 data={data}
//                                 imported={this.state.imported}
//                                 noResultFound={this.state.noResultFound}
//                                 changeSearchData={this.changeSearchData}
//                                 includeTitleImage={this.state.searchFromBookShare}
//                                 bookShareSearchToggle={this.state.searchFromBookShare}
//                                 lexileTabClicked={this.state.searchFromBookShare ? false : true}
//                                 storyId={this.props.id}
//                                 toggleSearch={this.state.searchFromBookShare}
//                                 searchFromBookShare={this.state.searchFromBookShare}
//                                 closeWizard={this.closeWizard}
//                                 hasBookShareCredentials={this.props.user.hasBookShareCredentials}
//                                 openModal={this.openModal}
//                             />
//                         }
//                         {this.state.browseTabClicked &&
//                             <DisplayStoryList
//                                 id={this.props.id}
//                                 type={
//                                     this.state.recomendTabClicked
//                                         ? 'Recomendations'
//                                         : this.state.lexileTabClicked
//                                             ? 'LexileStory'
//                                             : 'StudentStory'
//                                 }
//                                 storyList={storyList}
//                                 imported={this.state.imported}
//                                 storyId={this.props.id}
//                                 closeWizard={this.closeWizard}
//                             />
//                         }
//                         </DialogContent>
//                             {this.props.user && !this.props.user.hasBookShareCredentials &&
//                                 <DialogActions position='fixed' bottom='0'>
//                                     <div className={classes.browseBtnWrapper} style={{
//                                         align: "center",
//                                         backgroundColor: "white",
//                                         color: "grey",
//                                         width: "100%"
//                                     }}>
//                                         <Tab
//                                             label="Login to Bookshare"
//                                             className={classes.innerTabClicked}
//                                             onClick={this.loginBookshare}
//                                         />
//                                         { this.props.user.type=="Individual" &&
//                                             <Tab onClick={this.openBookshareAccess}
//                                                  className={classes.innerTabClicked}
//                                                  label="Click here to get access to Bookshare"/>
//                                         }
//                                     </div>
//                                 </DialogActions>
//                             }
//                             {this.props.user && this.props.user.hasBookShareCredentials &&
//                                 <DialogActions position='fixed' bottom='0'>
//                                     {/*<div className={classes.browseBtnWrapper} style={{*/}
//                                     {/*    align: "center",*/}
//                                     {/*    backgroundColor: "white",*/}
//                                     {/*    color: "grey",*/}
//                                     {/*    width: "100%"*/}
//                                     {/*}}>*/}
//                                     {/*    <Tab*/}
//                                     {/*        label="Logout from Bookshare"*/}
//                                     {/*        className={classes.innerTabClicked}*/}
//                                     {/*        onClick={this.logOutBookshare}*/}
//                                     {/*    />*/}
//                                     {/*    <div style={{*/}
//                                     {/*        paddingTop: "15px"*/}
//                                     {/*    }}>Logged in As: {this.props.bookShareIdentity}</div>*/}
//                                     {/*</div>*/}
//                                 </DialogActions>
//                             }
//                         </div>
//                     </Paper>
//                 </Dialog>
//                 <WizardToggleButton
//                     openWizard={this.openWizard}
//                     text={<FormattedMessage id='Databanks.browseStory' />}
//                     disabled={!this.props.databank}
//                 />
//                 {loginRequired && <BookShareCredentialsLogin
//                     openLoading={loginRequired}
//                     exitFunc={this.closeLoginModal} />}
//                 {
//                     this.state.openModal &&
//                     <LoadingModal openLoading={this.state.openModal} type={this.state.modalType} exitFunc={()=>{this.setState({ openModal: false, modalType: '' })}} />
//                 }
//                 {showBookshareAccess &&
//                     <Dialog open={showBookshareAccess} onClose={this.closeBookshareAccess} maxWidth="md" fullWidth>
//                         <DialogContent style={{ overflowY: 'auto' }}>
//                             <Grid container spacing={2}>
//                                 <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center'  }}>
//                                     <img
//                                         src="https://www.bookshare.org/cms/sites/default/files/styles/panopoly_image_original/public/images/media/current_version_bookshare_logo_transparent_background_with_tagline_horizontal_color.png?itok=2lZD6Hcw"
//                                         alt="Image 1"
//                                         style={{ width: '66%', height: 'auto' }}
//                                     />
//                                 </Grid>
//                                 <Grid item xs={6} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center'  }}>
//                                     <img
//                                         src="https://s3.us-east-2.amazonaws.com/oe-ui/BB-logo-oed.png"
//                                         alt="Image 2"
//                                         style={{ width: '66%', height: 'auto' }}
//                                     />
//                                 </Grid>
//                             </Grid>
//                             <div>
//                                 Bookshare is an ebook library of 1 million+ books that are available to qualifying individuals.  BuddyBooks partnership with Bookshare allows those with a Bookshare account to link their accounts and read books from Bookshare's catalogue within BuddyBooks.  For more information on how to qualify for a Bookshare account, click here: <a target={"_blank"} href='https://www.objectiveed.com/bookshare-test'>Bookshare Qualification</a>
//                             </div>
//                         </DialogContent>
//                     </Dialog>
//                 }
//             </>
//         )
//     }
}
export default connect(mapState)(withStyles(styles)(BrowseBook));


